import { useDroppable } from '@dnd-kit/core'
import {
  SortableContext,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import React from 'react'
import styled from 'styled-components/macro'
import CatchBoardCard from './CatchBoardCard'

/**
 * Catch Board column — droppable list + header count + value footer.
 */
const CatchBoardColumn = ({
  stage,
  meta,
  deals = [],
  dealCount,
  dealValueLabel = '—',
  potentialDealValueLabel,
  potentialDealValuePence = 0,
  onDealClick,
}) => {
  const count =
    dealCount != null ? Number(dealCount) || 0 : Array.isArray(deals) ? deals.length : 0
  const accent = meta?.columnAccent || '#5B8A72'
  const background = meta?.columnBackground || '#F4F8F6'
  const showPotential = Number(potentialDealValuePence) > 0
  const itemIds = deals.map(deal => deal.dealId)

  const { setNodeRef, isOver } = useDroppable({
    id: stage,
    data: { type: 'column', stage },
  })

  return (
    <Column
      data-testid={`catch-board-column-${stage}`}
      data-stage={stage}
      data-over={isOver ? 'true' : 'false'}
      style={{
        '--catch-board-column-accent': accent,
        '--catch-board-column-bg': background,
      }}
    >
      <Header>
        <HeaderTitle>
          <Emoji aria-hidden='true'>{meta?.emoji || ''}</Emoji>
          <TitleText>
            <Label>{meta?.label || stage}</Label>
            {meta?.subtitle ? <Subtitle>{meta.subtitle}</Subtitle> : null}
          </TitleText>
        </HeaderTitle>
        <CountBadge data-testid={`catch-board-column-count-${stage}`}>
          {count}
        </CountBadge>
      </Header>

      <SortableContext items={itemIds} strategy={verticalListSortingStrategy}>
        <CardList ref={setNodeRef} $isOver={isOver}>
          {deals.length === 0 ? (
            <EmptyColumn data-testid={`catch-board-column-empty-${stage}`}>
              Nothing here yet
            </EmptyColumn>
          ) : (
            deals.map(deal => (
              <CatchBoardCard
                key={deal.dealId}
                deal={deal}
                onDealClick={onDealClick}
              />
            ))
          )}
        </CardList>
      </SortableContext>

      <Footer
        data-testid={`catch-board-column-total-${stage}`}
        title={
          showPotential
            ? `Potential ${potentialDealValueLabel}`
            : 'Column deal value'
        }
      >
        <FooterLabel>Total</FooterLabel>
        <FooterValue>{dealValueLabel || '—'}</FooterValue>
        {showPotential ? (
          <FooterPotential>Potential {potentialDealValueLabel}</FooterPotential>
        ) : null}
      </Footer>
    </Column>
  )
}

const Column = styled.section`
  display: flex;
  flex-direction: column;
  min-width: 220px;
  max-width: 280px;
  flex: 1 0 220px;
  background: var(--catch-board-column-bg);
  border: 1px solid rgba(0, 0, 0, 0.06);
  border-top: 3px solid var(--catch-board-column-accent);
  border-radius: 8px;
  min-height: 280px;
`

const Header = styled.header`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 8px;
  padding: 12px 12px 10px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
`

const HeaderTitle = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  min-width: 0;
`

const Emoji = styled.span`
  font-size: 16px;
  line-height: 1.2;
`

const TitleText = styled.div`
  min-width: 0;
`

const Label = styled.div`
  font-size: 13px;
  font-weight: 700;
  color: ${({ theme }) => theme.colours.broccoliGreen || '#1a1a1a'};
`

const Subtitle = styled.div`
  margin-top: 2px;
  font-size: 11px;
  line-height: 1.3;
  color: ${({ theme }) => theme.colours.stormGrey || '#666'};
`

const CountBadge = styled.span`
  flex: 0 0 auto;
  min-width: 1.5rem;
  padding: 2px 7px;
  border-radius: 999px;
  text-align: center;
  font-size: 12px;
  font-weight: 700;
  color: #fff;
  background: var(--catch-board-column-accent);
`

const CardList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 10px 10px 12px;
  flex: 1 1 auto;
  min-height: 80px;
  outline: ${({ $isOver }) =>
    $isOver ? '2px dashed var(--catch-board-column-accent)' : 'none'};
  outline-offset: -4px;
  border-radius: 6px;
`

const EmptyColumn = styled.div`
  padding: 16px 8px;
  text-align: center;
  font-size: 12px;
  color: ${({ theme }) => theme.colours.oliveGrey || '#888'};
`

const Footer = styled.footer`
  display: grid;
  grid-template-columns: auto 1fr;
  gap: 2px 8px;
  margin-top: auto;
  padding: 10px 12px;
  border-top: 1px solid rgba(0, 0, 0, 0.08);
  background: rgba(255, 255, 255, 0.55);
  border-radius: 0 0 7px 7px;
`

const FooterLabel = styled.span`
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.02em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colours.stormGrey || '#666'};
`

const FooterValue = styled.span`
  justify-self: end;
  font-size: 13px;
  font-weight: 700;
  color: ${({ theme }) => theme.colours.broccoliGreen || '#1a1a1a'};
`

const FooterPotential = styled.span`
  grid-column: 1 / -1;
  font-size: 11px;
  color: ${({ theme }) => theme.colours.stormGrey || '#666'};
  text-align: right;
`

export default CatchBoardColumn
